import { useRef, useMemo, useState } from 'react'
import { useDrop } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';

import axios from 'axios'
import clsx from 'clsx'

import { KTSVG } from '../../../_metronic/helpers';

const API_URL = process.env.REACT_APP_API_URL

interface FileUploaderProps {
  mainTitle: string
  onFileUploaded : Function
  subTitle?: string
  fileIcon?: string
  acceptFiles: string
  allowMultiple? : boolean    
  fileName? : string
  onClearFile: Function
  required?: boolean
  disabled?: boolean
  subTitleClass?: string
}

export interface FileUploaderResponse {
  id: number, 
  name: string,
}


export function FileUploader({mainTitle, subTitle, fileIcon, acceptFiles, allowMultiple, onFileUploaded, fileName, onClearFile, required, disabled, subTitleClass} : FileUploaderProps) {

  const fileInput = useRef<HTMLInputElement>(null);
  const [status_message, setStatus] = useState('');
  const [isUploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const [ { canDrop, isOver}, dropRef] = useDrop({
    accept: [NativeTypes.FILE],
    drop: (item: any, monitor) => {        
      if (disabled === true || (! (item && item.hasOwnProperty('files') && Array.isArray(item.files)))) {
        return;
      }
      const { files } = item;                
      files.forEach((file: any) => {              
        processFile(file);                
      })                            
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  })

  const processFile = async (file: any) => {
    const data = new FormData()
    data.append('media', file);

    setStatus('');
    setProgress(0);
    setUploading(true);

    async function doFileUpload(payload: any) {
      await axios.post(
        `${API_URL}/UploadFileServlet`, 
        payload, 
        { 
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
              if (progressEvent && progressEvent.total) {
                  const progress = Math.floor((progressEvent.loaded / progressEvent.total) * 100);
                  setProgress(progress);
              }                        
          },
        }
      )            
      .then( res => {            
        if (res && res.data) {
          const { fileId, success, message } = res.data;
          if (! success) {                    
              setStatus(message ? message : 'File upload error');
              return;
          } 
          onFileUploaded({id : fileId, name: file.name})                    
        } else { 
          console.error(res);
        }                        
      })
      .catch(err => {
        console.error(err);
      })
      .finally(()=> {
        setUploading(false);
      })
    }

    await doFileUpload(data);    
  }

  const doClearFile = (e:any) => {
    e.stopPropagation();
    if (disabled === true) return;
    if (onClearFile) {
      onClearFile();
    }
  }

  const processFileUpload = (evt : any) => {
      
    const { files } = evt.target;
    // iterate files list
    if (files && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        const file = files.item(i)                
        processFile(file);
      }            
    }

    if( fileInput.current) {
      fileInput.current.value = '';
    }        
  };

  const okFiles = useMemo(() => {

    if (! acceptFiles) { 
      return '*.*';
    } else if (acceptFiles === 'ApplicantFileFormats') {
      return [
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",                                
        "application/pdf",
        "application/x-pdf",
        "application/vnd.ms-powerpoint",    // .ppt               
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",    // .pptx
      ].join(',');
      
    } else if (acceptFiles === 'UserFileFormats') {
      return [
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",                                
        "application/pdf",
        "application/x-pdf",
        "application/zip", 
        "application/x-zip-compressed", 
        "application/zip-compressed", 
        "multipart/x-zip" ,
        "application/vnd.ms-powerpoint",    // .ppt               
        "application/vnd.openxmlformats-officedocument.presentationml.presentation",    // .pptx
      ].join(',');            
    }

    return acceptFiles;

  }, [acceptFiles]);

  const MessageArea = () => {
    if (isUploading) {
      return <span className='text-muted fs-9'>Uploading file ...</span>
    }        
    if (status_message) {
      return <span className='text-danger fs-9'>{status_message}</span>
    }
    return <span className={subTitleClass ? subTitleClass : 'text-muted fs-9'}>{subTitle}</span>            
  };

  return (              
    <div className="position-relative mb-1">
      <div 
        ref={dropRef} 
        className={clsx("w-100 position-relative", {'bg-gray-200' : (canDrop && isOver && (disabled !== true))})}
        onClick={() => { 
          if (disabled === true || (! fileInput.current)) return;
          fileInput.current.click()
        }}
      >
        <input
          ref={fileInput}
          onChange={processFileUpload}
          type="file"
          style={{ display: "none" }}
          accept={okFiles}
          multiple={allowMultiple ? allowMultiple : false}
          disabled={(disabled === true)}
        />
        <label
          className={clsx('btn btn-outline btn-outline-dashed btn-outline-default py-1 px-2 d-flex flex-column', {'bg-gray-200': (disabled ===true)})}                
        >
          <div className="d-flex flex-row align-items-center lh-1">
            <KTSVG
              path={fileIcon ? fileIcon : (fileName ? '/media/icons/duotune/files/fil016.svg' : '/media/icons/duotune/files/fil018.svg')}
              className={'svg-icon-3x me-2 ' + (fileName ? 'text-primary' : '')}
            />
            <div className='fw-bold text-start lh-1 pe-5'>
              <div className={clsx(
                'text-dark fw-bolder d-block fs-7',
                {'required' : required}
              )}>
                {mainTitle}
              </div>
              <MessageArea />                            
            </div>                                        
          </div>                    
          {isUploading && 
          <div style={{height:"4px"}} className="my-1">
            <div className="progress" style={{height:"2px"}}>
                <div className="progress-bar progress-bar-sm progress-bar-striped progress-bar-animated " role="progressbar" style={{width: `${progress}%`}} aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}></div>
            </div>    
          </div>
          }                                    
        </label>
        { onClearFile &&             
        <div className="position-absolute btn-active-danger btn p-0 fs-10" style={{top:'0.25rem', right: '6px'}} onClick={doClearFile} title="Clear file">
          <KTSVG path='/media/icons/duotune/general/gen034.svg' className='svg-icon-2 m-0 lh-0' />  
        </div>
        }
      </div>            
    </div>
  )

}